import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";

import { Button, Modal, Input, Select, InputNumber, DatePicker, Switch, Spin, Empty, Form, Upload } from "antd";
import { SaveOutlined, CloseOutlined, InboxOutlined } from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import { PATH } from "../../redux/utils";

import {
  getPaciente,
  editarPaciente,
  subirFotoPaciente,
  eliminarPaciente,
  getTiposEstudio,
  getAseguradorasCatalogo,
  getAntecedentes,
  guardarAntecedentes,
  getPacienteAseguradoras,
  crearPacienteAseguradora,
  editarPacienteAseguradora,
  eliminarPacienteAseguradora,
  getPacienteResponsables,
  crearPacienteResponsable,
  editarPacienteResponsable,
  eliminarPacienteResponsable,
  getPacienteEstudios,
  getPacienteActividad,
  crearPacienteEstudio,
  editarPacienteEstudio,
  eliminarPacienteEstudio,
  getPacienteGraduaciones,
  crearPacienteGraduacion,
  editarPacienteGraduacion,
  eliminarPacienteGraduacion,
  getPacienteRecetas,
  crearPacienteReceta,
  editarPacienteReceta,
  eliminarPacienteReceta,
  getPacientePlanesVigilancia,
  crearPacientePlanVigilancia,
  editarPacientePlanVigilancia,
  eliminarPacientePlanVigilancia,
  getPacienteReferencias,
  crearPacienteReferencia,
  editarPacienteReferencia,
  eliminarPacienteReferencia,
} from "./pacientesApi";

import "./PacientesPage.css";
import { usePermisos } from "../../context/PermisosContext";

dayjs.locale("es");

const fmtFecha = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY") : "—";
};

const fmtFechaHora = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const ACTION_CONFIG = {
  CREATE: { label: "Creación", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualización", color: "var(--brand-primary)", bg: "#dbeafe" },
  DELETE: { label: "Eliminación", color: "#b91c1c", bg: "#fee2e2" },
};
const getActionConfig = (action) =>
  ACTION_CONFIG[action] || { label: action || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

const calcularEdad = (fechaNacimiento) => {
  if (!fechaNacimiento) return null;
  const d = dayjs(fechaNacimiento);
  if (!d.isValid()) return null;
  return dayjs().diff(d, "year");
};

// =====================================================================
// Tabs genericos de tipo "lista + modal" (Aseguradoras, Responsables,
// Estudios diagnosticos, Recetas, Planes de vigilancia, Referencias):
// todas siguen el mismo patron CRUD, solo cambian los campos.
// =====================================================================
function SimpleCrudTab({
  idPaciente,
  icon,
  title,
  emptyText,
  addLabel,
  fetchList,
  createItem,
  updateItem,
  deleteItem,
  idField,
  columns,
  fields,
  extraOptions,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const { perm } = usePermisos() || { perm: () => true };

  const load = useCallback(() => {
    setLoading(true);
    fetchList(idPaciente)
      .then((res) => setItems(res.data ?? []))
      .catch(() => toast.error(`No se pudo cargar ${title.toLowerCase()}`))
      .finally(() => setLoading(false));
  }, [idPaciente]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingRow(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    const initial = {};
    fields.forEach((f) => {
      if (f.type === "info" || f.type === "file") return;
      let v = row[f.name];
      if (f.type === "date" && v) v = dayjs(v);
      initial[f.name] = v;
    });
    form.setFieldsValue(initial);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const hasFile = fields.some((f) => f.type === "file");
    const payload = hasFile ? new FormData() : {};
    fields.forEach((f) => {
      if (f.type === "info") return;
      let v = values[f.name];
      if (f.type === "file") {
        const raw = v?.[0]?.originFileObj;
        if (raw) payload.append(f.name, raw);
        return;
      }
      if (f.type === "date") v = v ? v.format("YYYY-MM-DD") : null;
      if (f.type === "switch") v = Boolean(v);
      if (hasFile) {
        if (v !== undefined && v !== null) payload.append(f.name, v);
      } else {
        payload[f.name] = v === undefined ? null : v;
      }
    });

    setSaving(true);
    try {
      if (editingRow) {
        await updateItem(editingRow[idField], payload);
        toast.success("Registro actualizado");
      } else {
        await createItem(idPaciente, payload);
        toast.success("Registro agregado");
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar el registro");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (row) => {
    Modal.confirm({
      title: "¿Eliminar este registro?",
      content: "Esta accion no se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await deleteItem(row[idField]);
          toast.success("Registro eliminado");
          load();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        }
      },
    });
  };

  return (
    <div className="cd-card">
      <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="cd-card-icon-wrap">{icon}</div>
          <h2 className="cd-card-title">{title}</h2>
          <span className="cd-actividad-count">{items.length}</span>
        </div>
        {perm("pacientes", "insertar") && (
          <Button
          type="primary"
          icon={<i className="fa-solid fa-plus" />}
          onClick={openCreate}
          style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
        >
          {addLabel}
        </Button>
        )}
      </div>

      {loading ? (
        <div className="cd-loading-wrap"><Spin /></div>
      ) : items.length === 0 ? (
        <Empty description={emptyText} />
      ) : (
        <div className="cd-pagos-list">
          {items.map((row) => (
            <div
              key={row[idField]}
              className="cd-pagos-row"
              style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr) 90px` }}
            >
              {columns.map((col) => (
                <div key={col.label}>
                  <span className="cd-field-label">{col.label}</span>
                  <div className="cd-field-value">{col.render(row)}</div>
                </div>
              ))}
              <div className="cd-row-actions">
                {perm("pacientes", "editar") && (
                  <Button className="cd-btn-edit" onClick={() => openEdit(row)}>Editar</Button>
                )}
                {perm("pacientes", "eliminar") && (
                  <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" onClick={() => handleDelete(row)}>Eliminar</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        okText={editingRow ? "Guardar" : "Agregar"}
        cancelText="Cancelar"
        title={`${editingRow ? "Editar" : "Agregar"} ${title.toLowerCase()}`}
        confirmLoading={saving}
        centered
        styles={{ body: { maxHeight: "calc(100vh - 220px)", overflowY: "auto", paddingRight: 8 } }}
        okButtonProps={{ style: { background: "var(--brand-primary)", borderColor: "var(--brand-primary)" } }}
      >
        <Form form={form} layout="vertical" size="small">
          {fields.map((f) => f.type === "info" ? (
            <Form.Item key={f.name} label={f.label} shouldUpdate>
              {() => {
                const opt = (f.options || []).find((o) => o.value === form.getFieldValue(f.watch));
                return <Input value={opt ? (opt.convenio_directo ? "Si" : "No") : "—"} disabled />;
              }}
            </Form.Item>
          ) : (
            <Form.Item
              key={f.name}
              name={f.name}
              label={f.label}
              rules={f.required && !(f.type === "file" && editingRow) ? [{ required: true, message: f.type === "file" ? "Selecciona un archivo" : "Requerido" }] : []}
              valuePropName={f.type === "switch" ? "checked" : f.type === "file" ? "fileList" : "value"}
              getValueFromEvent={f.type === "file" ? (e) => (Array.isArray(e) ? e : e?.fileList?.slice(-1)) : undefined}
              extra={f.type === "file" && editingRow?.filename ? `Archivo actual: ${editingRow.filename}. Suelta uno nuevo solo si quieres reemplazarlo.` : undefined}
            >
              {f.type === "file" ? (
                <Upload.Dragger
                  maxCount={1}
                  beforeUpload={() => false}
                  accept={f.accept}
                >
                  <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                  <p className="ant-upload-text">Arrastra el archivo aqui o haz clic para seleccionarlo</p>
                  <p className="ant-upload-hint">Imagen o PDF</p>
                </Upload.Dragger>
              ) : f.type === "select" ? (
                <Select
                  placeholder={f.placeholder || "Selecciona una opcion"}
                  options={f.options || extraOptions?.[f.optionsKey] || []}
                  showSearch
                  optionFilterProp="label"
                />
              ) : f.type === "date" ? (
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              ) : f.type === "number" ? (
                <InputNumber style={{ width: "100%" }} min={f.min ?? 0} placeholder={f.placeholder} />
              ) : f.type === "switch" ? (
                <Switch />
              ) : f.type === "textarea" ? (
                <Input.TextArea rows={3} placeholder={f.placeholder} />
              ) : (
                <Input placeholder={f.placeholder} autoComplete="off" />
              )}
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </div>
  );
}

// =====================================================================
// Componente principal
// =====================================================================
export default function PacienteDetallePage() {
  const { idPaciente } = useParams();
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };

  const [paciente, setPaciente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("info");

  const [editingInfo, setEditingInfo] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [tiposEstudio, setTiposEstudio] = useState([]);
  const [aseguradorasCat, setAseguradorasCat] = useState([]);
  const [subiendoFoto, setSubiendoFoto] = useState(false);

  // Antecedentes (registro "vigente", siempre se guarda como uno nuevo)
  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);
  const [antecedentes, setAntecedentes] = useState(null);
  const [loadingAntecedentes, setLoadingAntecedentes] = useState(false);
  const [editingAntecedentes, setEditingAntecedentes] = useState(false);
  const [savingAntecedentes, setSavingAntecedentes] = useState(false);
  const [antecedentesForm, setAntecedentesForm] = useState({});

  // Graduaciones (formulario clinico propio)
  const [graduaciones, setGraduaciones] = useState([]);
  const [loadingGraduaciones, setLoadingGraduaciones] = useState(false);
  const [graduacionModalOpen, setGraduacionModalOpen] = useState(false);
  const [editingGraduacion, setEditingGraduacion] = useState(null);
  const [savingGraduacion, setSavingGraduacion] = useState(false);
  const [graduacionForm] = Form.useForm();

  const fetchPaciente = useCallback(() => {
    setLoading(true);
    getPaciente(idPaciente)
      .then((res) => setPaciente(res.data))
      .catch(() => toast.error("No se pudo cargar el paciente"))
      .finally(() => setLoading(false));
  }, [idPaciente]);

  useEffect(() => { fetchPaciente(); }, [fetchPaciente]);

  useEffect(() => {
    getTiposEstudio().then((r) => setTiposEstudio((r.data ?? []).map((t) => ({ label: t.nombre, value: t.id_tipo_estudio })))).catch(() => {});
    getAseguradorasCatalogo().then((r) => setAseguradorasCat((r.data ?? []).map((a) => ({ label: a.nombre, value: a.id_aseguradora, convenio_directo: Boolean(a.convenio_directo) })))).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab !== "info" || !idPaciente) return;
    setLoadingAntecedentes(true);
    getAntecedentes(idPaciente)
      .then((res) => setAntecedentes(res.data && Object.keys(res.data).length ? res.data : null))
      .catch(() => {})
      .finally(() => setLoadingAntecedentes(false));
  }, [activeTab, idPaciente]);

  useEffect(() => {
    if (activeTab !== "actividad" || !idPaciente) return;
    setLoadingActividad(true);
    getPacienteActividad(idPaciente)
      .then((res) => setActividad(Array.isArray(res.data) ? res.data : []))
      .catch(() => setActividad([]))
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idPaciente]);

  const loadGraduaciones = useCallback(() => {
    setLoadingGraduaciones(true);
    getPacienteGraduaciones(idPaciente)
      .then((res) => setGraduaciones(res.data ?? []))
      .catch(() => toast.error("No se pudieron cargar las graduaciones"))
      .finally(() => setLoadingGraduaciones(false));
  }, [idPaciente]);

  useEffect(() => {
    if (activeTab !== "graduaciones") return;
    loadGraduaciones();
  }, [activeTab, loadGraduaciones]);

  const startEditInfo = () => {
    setEditForm({
      nombre_completo: paciente.nombre_completo,
      fecha_nacimiento: paciente.fecha_nacimiento ? dayjs(paciente.fecha_nacimiento) : undefined,
      telefono: paciente.telefono || "",
      correo: paciente.correo || "",
      direccion: paciente.direccion || "",
    });
    setEditingInfo(true);
  };

  const cancelEditInfo = () => setEditingInfo(false);

  const handleSaveInfo = async () => {
    if (!editForm.nombre_completo?.trim() || !editForm.telefono?.trim() || !editForm.fecha_nacimiento) {
      toast.warning("Nombre, telefono y fecha de nacimiento son obligatorios");
      return;
    }
    setSavingInfo(true);
    try {
      const payload = {
        nombre_completo: editForm.nombre_completo.trim(),
        fecha_nacimiento: editForm.fecha_nacimiento.format("YYYY-MM-DD"),
        telefono: editForm.telefono.trim(),
        correo: editForm.correo?.trim() || null,
        direccion: editForm.direccion?.trim() || null,
      };
      await editarPaciente(idPaciente, payload);
      toast.success("Paciente actualizado correctamente");
      setEditingInfo(false);
      fetchPaciente();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingInfo(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "¿Eliminar este paciente?",
      content: "Esta accion desactiva al paciente. No se puede deshacer desde aqui.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setDeleting(true);
        try {
          await eliminarPaciente(idPaciente);
          toast.success("Paciente eliminado");
          navigate("/app/pacientes");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  // ── Antecedentes ──
  const startEditAntecedentes = () => {
    setAntecedentesForm({
      condiciones_medicas: antecedentes?.condiciones_medicas || "",
      alergias: antecedentes?.alergias || "",
      medicamentos_actuales: antecedentes?.medicamentos_actuales || "",
    });
    setEditingAntecedentes(true);
  };

  const handleSaveAntecedentes = async () => {
    setSavingAntecedentes(true);
    try {
      await guardarAntecedentes(idPaciente, antecedentesForm);
      toast.success("Antecedentes actualizados");
      setEditingAntecedentes(false);
      const res = await getAntecedentes(idPaciente);
      setAntecedentes(res.data && Object.keys(res.data).length ? res.data : null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron guardar los antecedentes");
    } finally {
      setSavingAntecedentes(false);
    }
  };

  // ── Graduaciones ──
  const openCreateGraduacion = () => {
    setEditingGraduacion(null);
    graduacionForm.resetFields();
    setGraduacionModalOpen(true);
  };

  const GRADUACION_NUM_FIELDS = [
    "od_esfera", "od_cilindro", "od_eje", "oi_esfera", "oi_cilindro", "oi_eje",
    "adicion", "curva_base", "diametro",
  ];

  const openEditGraduacion = (row) => {
    setEditingGraduacion(row);
    const values = { ...row };
    GRADUACION_NUM_FIELDS.forEach((f) => {
      values[f] = row[f] != null && row[f] !== "" ? Number(row[f]) : undefined;
    });
    graduacionForm.setFieldsValue(values);
    setGraduacionModalOpen(true);
  };

  const closeGraduacionModal = () => {
    setGraduacionModalOpen(false);
    setEditingGraduacion(null);
    graduacionForm.resetFields();
  };

  const handleSaveGraduacion = async () => {
    let values;
    try {
      values = await graduacionForm.validateFields();
    } catch {
      return;
    }
    setSavingGraduacion(true);
    try {
      if (editingGraduacion) {
        await editarPacienteGraduacion(editingGraduacion.id_graduacion, values);
        toast.success("Graduacion actualizada");
      } else {
        await crearPacienteGraduacion(idPaciente, values);
        toast.success("Graduacion agregada");
      }
      closeGraduacionModal();
      loadGraduaciones();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar la graduacion");
    } finally {
      setSavingGraduacion(false);
    }
  };

  const handleDeleteGraduacion = (row) => {
    Modal.confirm({
      title: "¿Eliminar esta graduacion?",
      content: "Esta accion no se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await eliminarPacienteGraduacion(row.id_graduacion);
          toast.success("Graduacion eliminada");
          loadGraduaciones();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!paciente) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Paciente no encontrado" />
      </div>
    );
  }

  const edad = calcularEdad(paciente.fecha_nacimiento);
  const activo = paciente.active === undefined ? true : Boolean(paciente.active);
  const fotoUrl = paciente.path && paciente.filename ? `${PATH.replace(/\/$/, "")}/${paciente.path}/${paciente.filename}` : null;

  const handleFotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/") || file.size > 5 * 1024 * 1024) {
      toast.warning("Selecciona una imagen de maximo 5 MB");
      return;
    }
    setSubiendoFoto(true);
    try {
      await subirFotoPaciente(idPaciente, file);
      toast.success("Foto actualizada");
      fetchPaciente();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir la foto");
    } finally {
      setSubiendoFoto(false);
    }
  };

  const TABS = [
    { key: "info", label: "Informacion", icon: "fa-file-lines" },
    { key: "aseguradoras", label: "Aseguradoras", icon: "fa-shield-heart" },
    { key: "responsables", label: "Responsables", icon: "fa-people-group" },
    { key: "estudios", label: "Estudios diagnosticos", icon: "fa-file-waveform" },
    { key: "graduaciones", label: "Graduaciones de lentes", icon: "fa-glasses" },
    { key: "recetas", label: "Recetas", icon: "fa-prescription" },
    { key: "vigilancia", label: "Planes de vigilancia", icon: "fa-eye" },
    { key: "referencias", label: "Referencias", icon: "fa-share-nodes" },
    { key: "actividad", label: "Actividad", icon: "fa-clock" },
  ];

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">{(paciente.nombre_completo || "—").toUpperCase()}</h1>
                <span className={`cd-status-badge ${activo ? "cd-status-active" : "cd-status-inactive"}`}>
                  {activo ? "Activo" : "Inactivo"}
                </span>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item">
                  <i className="fa-solid fa-calendar" />
                  Registrado el {fmtFechaHora(paciente.datetime)}
                </span>
                {paciente.creado_por && (
                  <span className="cd-meta-item">
                    <i className="fa-solid fa-user" />
                    {capitalize(paciente.creado_por)}
                  </span>
                )}
                {edad != null && (
                  <span className="cd-meta-item">
                    <i className="fa-solid fa-cake-candles" />
                    {edad} años
                  </span>
                )}
              </div>
            </div>

            <div className="cd-header-actions">
              {perm("pacientes", "eliminar") && (
                <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" loading={deleting} onClick={handleDelete}>
                Eliminar
              </Button>
              )}
            </div>
          </div>

          <div className="cd-header-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`cd-tab-btn ${activeTab === t.key ? "cd-tab-btn-active" : ""}`}
                onClick={() => setActiveTab(t.key)}
              >
                <i className={`fa-solid ${t.icon}`} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "info" && (
          <div className="cd-info-layout">
            <div className="cd-foto-card">
              <div className="cd-foto-frame">
                {fotoUrl ? <img src={fotoUrl} alt={paciente.nombre_completo} /> : <i className="fa-solid fa-user" />}
              </div>
              {perm("pacientes", "editar") && (
                <label className={`cd-foto-btn ${subiendoFoto ? "cd-foto-btn-disabled" : ""}`}>
                  <input type="file" accept="image/*" hidden disabled={subiendoFoto} onChange={handleFotoChange} />
                  <i className="fa-solid fa-camera" /> {subiendoFoto ? "Subiendo..." : fotoUrl ? "Cambiar foto" : "Subir foto"}
                </label>
              )}
            </div>
          <div className="cd-bento-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", alignItems: "start", flex: 1, minWidth: 0 }}>
            <div className="cd-card">
              <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="cd-card-icon-wrap"><i className="fa-solid fa-user"></i></div>
                  <h2 className="cd-card-title">Datos generales</h2>
                </div>
                {!editingInfo && perm("pacientes", "editar") && (
                  <Button className="cd-btn-edit" onClick={startEditInfo}>
                    Editar
                  </Button>
                )}
              </div>

              {editingInfo ? (
                <div className="cd-edit-form">
                  <div className="cd-edit-grid">
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Nombre completo</span>
                      <Input value={editForm.nombre_completo} onChange={(e) => setEditForm((f) => ({ ...f, nombre_completo: e.target.value }))} />
                    </div>
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Fecha de nacimiento</span>
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                        value={editForm.fecha_nacimiento}
                        onChange={(v) => setEditForm((f) => ({ ...f, fecha_nacimiento: v }))}
                      />
                    </div>
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Telefono</span>
                      <Input value={editForm.telefono} onChange={(e) => setEditForm((f) => ({ ...f, telefono: e.target.value }))} />
                    </div>
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Correo</span>
                      <Input value={editForm.correo} onChange={(e) => setEditForm((f) => ({ ...f, correo: e.target.value }))} placeholder="Opcional" />
                    </div>
                    <div className="cd-edit-field" style={{ gridColumn: "1 / -1" }}>
                      <span className="cd-edit-label">Direccion</span>
                      <Input.TextArea rows={2} value={editForm.direccion} onChange={(e) => setEditForm((f) => ({ ...f, direccion: e.target.value }))} />
                    </div>
                  </div>
                  <div className="cd-edit-actions">
                    <Button icon={<CloseOutlined />} onClick={cancelEditInfo} disabled={savingInfo}>Cancelar</Button>
                    <Button type="primary" icon={<SaveOutlined />} loading={savingInfo} onClick={handleSaveInfo} style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}>
                      Guardar
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                  <div>
                    <span className="cd-field-label">Nombre completo</span>
                    <span className="cd-field-value">{capitalize(paciente.nombre_completo) || "—"}</span>
                  </div>
                  <div>
                    <span className="cd-field-label">Fecha de nacimiento</span>
                    <span className="cd-field-value">{fmtFecha(paciente.fecha_nacimiento)}{edad != null ? ` (${edad} años)` : ""}</span>
                  </div>
                  <div>
                    <span className="cd-field-label">Telefono</span>
                    <span className="cd-field-value">{paciente.telefono || "—"}</span>
                  </div>
                  <div>
                    <span className="cd-field-label">Correo</span>
                    <span className="cd-field-value">{paciente.correo || "—"}</span>
                  </div>
                  <div>
                    <span className="cd-field-label">Fecha de registro</span>
                    <span className="cd-field-value">{fmtFechaHora(paciente.datetime)}</span>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <span className="cd-field-label">Direccion</span>
                    <span className="cd-field-value" style={{ fontWeight: 400, color: "#595c5e", lineHeight: 1.5 }}>
                      {paciente.direccion || "—"}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="cd-card">
              <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="cd-card-icon-wrap"><i className="fa-solid fa-notes-medical"></i></div>
                  <h2 className="cd-card-title">Antecedentes clinicos</h2>
                </div>
                {!editingAntecedentes && perm("pacientes", "editar") && (
                    <Button className="cd-btn-edit" onClick={startEditAntecedentes}>
                    Editar
                  </Button>
                )}
              </div>

              {loadingAntecedentes ? (
                <div className="cd-loading-wrap"><Spin /></div>
              ) : editingAntecedentes ? (
                <div className="cd-edit-form">
                  <div className="cd-edit-grid" style={{ gridTemplateColumns: "1fr" }}>
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Condiciones medicas</span>
                      <Input.TextArea
                        rows={2}
                        value={antecedentesForm.condiciones_medicas}
                        onChange={(e) => setAntecedentesForm((f) => ({ ...f, condiciones_medicas: e.target.value }))}
                        placeholder="Ej. Diabetes, hipertension..."
                      />
                    </div>
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Alergias</span>
                      <Input.TextArea
                        rows={2}
                        value={antecedentesForm.alergias}
                        onChange={(e) => setAntecedentesForm((f) => ({ ...f, alergias: e.target.value }))}
                        placeholder="Ej. Penicilina, polen..."
                      />
                    </div>
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Medicamentos actuales</span>
                      <Input.TextArea
                        rows={2}
                        value={antecedentesForm.medicamentos_actuales}
                        onChange={(e) => setAntecedentesForm((f) => ({ ...f, medicamentos_actuales: e.target.value }))}
                        placeholder="Ej. Losartan 50mg cada 24h..."
                      />
                    </div>
                  </div>
                  <div className="cd-edit-actions">
                    <Button icon={<CloseOutlined />} onClick={() => setEditingAntecedentes(false)} disabled={savingAntecedentes}>Cancelar</Button>
                    <Button type="primary" icon={<SaveOutlined />} loading={savingAntecedentes} onClick={handleSaveAntecedentes} style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}>
                      Guardar
                    </Button>
                  </div>
                </div>
              ) : antecedentes ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  <div>
                    <span className="cd-field-label">Condiciones medicas</span>
                    <span className="cd-field-value" style={{ fontWeight: 400, color: "#595c5e", lineHeight: 1.5 }}>
                      {antecedentes.condiciones_medicas || "Sin registrar"}
                    </span>
                  </div>
                  <div>
                    <span className="cd-field-label">Alergias</span>
                    <span className="cd-field-value" style={{ fontWeight: 400, color: "#595c5e", lineHeight: 1.5 }}>
                      {antecedentes.alergias || "Sin registrar"}
                    </span>
                  </div>
                  <div>
                    <span className="cd-field-label">Medicamentos actuales</span>
                    <span className="cd-field-value" style={{ fontWeight: 400, color: "#595c5e", lineHeight: 1.5 }}>
                      {antecedentes.medicamentos_actuales || "Sin registrar"}
                    </span>
                  </div>
                  <div>
                    <span className="cd-field-label">Ultima actualizacion</span>
                    <span className="cd-field-value">{fmtFechaHora(antecedentes.fecha)}</span>
                  </div>
                </div>
              ) : (
                <Empty description="Este paciente todavia no tiene antecedentes registrados" />
              )}
            </div>
          </div>
          </div>
        )}

        {activeTab === "aseguradoras" && (
          <SimpleCrudTab
            idPaciente={idPaciente}
            icon={<i className="fa-solid fa-shield-heart" />}
            title="Aseguradoras"
            addLabel="Agregar aseguradora"
            emptyText="Este paciente todavia no tiene aseguradoras registradas"
            fetchList={getPacienteAseguradoras}
            createItem={crearPacienteAseguradora}
            updateItem={editarPacienteAseguradora}
            deleteItem={eliminarPacienteAseguradora}
            idField="id_paciente_aseguradora"
            columns={[
              { label: "Aseguradora", render: (r) => r.aseguradora_nombre || "—" },
              { label: "Numero de poliza", render: (r) => r.numero_poliza || "—" },
              { label: "Convenio directo", render: (r) => (r.convenio_directo ? "Si" : "No") },
            ]}
            fields={[
              { name: "id_aseguradora", label: "Aseguradora", type: "select", options: aseguradorasCat, required: true },
              { name: "convenio_directo", label: "Convenio directo", type: "info", watch: "id_aseguradora", options: aseguradorasCat },
              { name: "numero_poliza", label: "Numero de poliza", type: "text", placeholder: "Opcional" },
            ]}
          />
        )}

        {activeTab === "responsables" && (
          <SimpleCrudTab
            idPaciente={idPaciente}
            icon={<i className="fa-solid fa-people-group" />}
            title="Responsables"
            addLabel="Agregar responsable"
            emptyText="Este paciente todavia no tiene responsables registrados"
            fetchList={getPacienteResponsables}
            createItem={crearPacienteResponsable}
            updateItem={editarPacienteResponsable}
            deleteItem={eliminarPacienteResponsable}
            idField="id_responsable"
            columns={[
              { label: "Nombre", render: (r) => r.nombre },
              { label: "Telefono", render: (r) => r.telefono },
              { label: "Parentesco", render: (r) => r.parentesco },
              { label: "Tipo", render: (r) => (r.tipo === "responsable_menor" ? "Responsable de menor" : "Referencia") },
            ]}
            fields={[
              { name: "nombre", label: "Nombre completo", type: "text", required: true },
              { name: "telefono", label: "Telefono", type: "text", required: true },
              { name: "parentesco", label: "Parentesco", type: "text", required: true, placeholder: "Ej. Madre, padre, tutor..." },
              {
                name: "tipo", label: "Tipo", type: "select", required: true,
                options: [
                  { label: "Responsable de menor", value: "responsable_menor" },
                  { label: "Referencia", value: "referencia" },
                ],
              },
            ]}
          />
        )}

        {activeTab === "estudios" && (
          <SimpleCrudTab
            idPaciente={idPaciente}
            icon={<i className="fa-solid fa-file-waveform" />}
            title="Estudios diagnosticos"
            addLabel="Agregar estudio"
            emptyText="Este paciente todavia no tiene estudios diagnosticos registrados"
            fetchList={getPacienteEstudios}
            createItem={crearPacienteEstudio}
            updateItem={editarPacienteEstudio}
            deleteItem={eliminarPacienteEstudio}
            idField="id_estudio"
            columns={[
              { label: "Tipo de estudio", render: (r) => r.tipo_estudio_nombre || "—" },
              {
                label: "Archivo",
                render: (r) => r.path
                  ? <a href={`${PATH.replace(/\/$/, "")}/${r.path}`} target="_blank" rel="noreferrer">{r.filename || "Ver archivo"}</a>
                  : (r.filename || "—"),
              },
              { label: "Fecha", render: (r) => fmtFecha(r.fecha) },
              { label: "Interpretacion", render: (r) => r.interpretacion || "—" },
            ]}
            fields={[
              { name: "id_tipo_estudio", label: "Tipo de estudio", type: "select", options: tiposEstudio, required: true },
              { name: "file", label: "Archivo del estudio", type: "file", required: true, accept: "image/*,.pdf" },
              { name: "interpretacion", label: "Interpretacion clinica", type: "textarea", placeholder: "Opcional" },
            ]}
          />
        )}

        {activeTab === "graduaciones" && (
          <div className="cd-card">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-glasses"></i></div>
                <h2 className="cd-card-title">Graduaciones de lentes</h2>
                <span className="cd-actividad-count">{graduaciones.length}</span>
              </div>
              {perm("pacientes", "insertar") && (
                <Button
                type="primary"
                icon={<i className="fa-solid fa-plus" />}
                onClick={openCreateGraduacion}
                style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
              >
                Agregar graduacion
              </Button>
              )}
            </div>

            {loadingGraduaciones ? (
              <div className="cd-loading-wrap"><Spin /></div>
            ) : graduaciones.length === 0 ? (
              <Empty description="Este paciente todavia no tiene graduaciones registradas" />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {graduaciones.map((g) => (
                  <div key={g.id_graduacion} style={{ background: "#fbfcfe", border: "1px solid #f0f2f5", borderRadius: 12, padding: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "#2c2f31" }}>
                          {g.tipo_correccion === "contacto" ? "Lentes de contacto" : "Armazon"} · {fmtFecha(g.fecha)}
                        </div>
                        <div style={{ fontSize: 12, color: "#595c5e" }}>
                          Motivo: {g.motivo_consulta === "rutina" ? "Consulta de rutina" : "Molestia especifica"}
                          {g.doctor_nombre ? ` · Dr(a). ${capitalize(g.doctor_nombre)}` : ""}
                        </div>
                      </div>
                      <div className="cd-row-actions">
                        {perm("pacientes", "editar") && (
                          <Button className="cd-btn-edit" onClick={() => openEditGraduacion(g)}>Editar</Button>
                        )}
                        {perm("pacientes", "eliminar") && (
                          <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" onClick={() => handleDeleteGraduacion(g)}>Eliminar</Button>
                        )}
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 10 }}>
                      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
                        <div className="cd-field-label">OD (ojo derecho)</div>
                        <div className="cd-field-value">
                          Esfera {g.od_esfera ?? "—"} · Cil. {g.od_cilindro ?? "—"} · Eje {g.od_eje ?? "—"}
                        </div>
                      </div>
                      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
                        <div className="cd-field-label">OI (ojo izquierdo)</div>
                        <div className="cd-field-value">
                          Esfera {g.oi_esfera ?? "—"} · Cil. {g.oi_cilindro ?? "—"} · Eje {g.oi_eje ?? "—"}
                        </div>
                      </div>
                      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
                        <div className="cd-field-label">Adicion / curva base / diametro</div>
                        <div className="cd-field-value">
                          {g.adicion ?? "—"} / {g.curva_base ?? "—"} / {g.diametro ?? "—"}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
                      <div>
                        <span className="cd-field-label">Agudeza OD (sin / con correccion)</span>
                        <span className="cd-field-value">{g.agudeza_od_sin_correccion || "—"} / {g.agudeza_od_con_correccion || "—"}</span>
                      </div>
                      <div>
                        <span className="cd-field-label">Agudeza OI (sin / con correccion)</span>
                        <span className="cd-field-value">{g.agudeza_oi_sin_correccion || "—"} / {g.agudeza_oi_con_correccion || "—"}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Modal
              open={graduacionModalOpen}
              onCancel={closeGraduacionModal}
              onOk={handleSaveGraduacion}
              okText={editingGraduacion ? "Guardar" : "Agregar"}
              cancelText="Cancelar"
              title={editingGraduacion ? "Editar graduacion" : "Nueva graduacion de lentes"}
              confirmLoading={savingGraduacion}
              centered
              width={640}
              okButtonProps={{ style: { background: "var(--brand-primary)", borderColor: "var(--brand-primary)" } }}
            >
              <Form form={graduacionForm} layout="vertical">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                  <Form.Item name="tipo_correccion" label="Tipo de correccion" rules={[{ required: true, message: "Requerido" }]}>
                    <Select
                      placeholder="Selecciona"
                      options={[{ label: "Armazon", value: "armazon" }, { label: "Lentes de contacto", value: "contacto" }]}
                    />
                  </Form.Item>
                  <Form.Item name="motivo_consulta" label="Motivo de consulta" rules={[{ required: true, message: "Requerido" }]}>
                    <Select
                      placeholder="Selecciona"
                      options={[{ label: "Consulta de rutina", value: "rutina" }, { label: "Molestia especifica", value: "molestia_especifica" }]}
                    />
                  </Form.Item>
                </div>

                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", color: "#6b7280", textTransform: "uppercase", margin: "8px 0" }}>
                  OD (ojo derecho)
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 12px" }}>
                  <Form.Item name="od_esfera" label="Esfera"><InputNumber style={{ width: "100%" }} step={0.25} /></Form.Item>
                  <Form.Item name="od_cilindro" label="Cilindro"><InputNumber style={{ width: "100%" }} step={0.25} /></Form.Item>
                  <Form.Item name="od_eje" label="Eje"><InputNumber style={{ width: "100%" }} min={0} max={180} /></Form.Item>
                </div>

                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", color: "#6b7280", textTransform: "uppercase", margin: "8px 0" }}>
                  OI (ojo izquierdo)
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 12px" }}>
                  <Form.Item name="oi_esfera" label="Esfera"><InputNumber style={{ width: "100%" }} step={0.25} /></Form.Item>
                  <Form.Item name="oi_cilindro" label="Cilindro"><InputNumber style={{ width: "100%" }} step={0.25} /></Form.Item>
                  <Form.Item name="oi_eje" label="Eje"><InputNumber style={{ width: "100%" }} min={0} max={180} /></Form.Item>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 12px" }}>
                  <Form.Item name="adicion" label="Adicion"><InputNumber style={{ width: "100%" }} step={0.25} /></Form.Item>
                  <Form.Item name="curva_base" label="Curva base"><InputNumber style={{ width: "100%" }} step={0.1} /></Form.Item>
                  <Form.Item name="diametro" label="Diametro"><InputNumber style={{ width: "100%" }} step={0.1} /></Form.Item>
                </div>

                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", color: "#6b7280", textTransform: "uppercase", margin: "8px 0" }}>
                  Agudeza visual
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                  <Form.Item name="agudeza_od_sin_correccion" label="OD sin correccion"><Input placeholder="Ej. 20/40" /></Form.Item>
                  <Form.Item name="agudeza_od_con_correccion" label="OD con correccion"><Input placeholder="Ej. 20/20" /></Form.Item>
                  <Form.Item name="agudeza_oi_sin_correccion" label="OI sin correccion"><Input placeholder="Ej. 20/40" /></Form.Item>
                  <Form.Item name="agudeza_oi_con_correccion" label="OI con correccion"><Input placeholder="Ej. 20/20" /></Form.Item>
                </div>
              </Form>
            </Modal>
          </div>
        )}

        {activeTab === "recetas" && (
          <SimpleCrudTab
            idPaciente={idPaciente}
            icon={<i className="fa-solid fa-prescription" />}
            title="Recetas"
            addLabel="Agregar receta"
            emptyText="Este paciente todavia no tiene recetas registradas"
            fetchList={getPacienteRecetas}
            createItem={crearPacienteReceta}
            updateItem={editarPacienteReceta}
            deleteItem={eliminarPacienteReceta}
            idField="id_receta"
            columns={[
              { label: "Medicamento", render: (r) => r.medicamento },
              { label: "Dosis / indicaciones", render: (r) => r.dosis_indicaciones },
              { label: "Fecha de inicio", render: (r) => fmtFecha(r.fecha_inicio) },
              { label: "Estado", render: (r) => capitalize(r.estado) },
            ]}
            fields={[
              { name: "medicamento", label: "Medicamento", type: "text", required: true },
              { name: "dosis_indicaciones", label: "Dosis e indicaciones", type: "textarea", required: true },
              { name: "motivo", label: "Motivo", type: "text", required: true },
              { name: "fecha_inicio", label: "Fecha de inicio", type: "date", required: true },
              { name: "fecha_fin", label: "Fecha de fin", type: "date" },
              { name: "indefinido", label: "Tratamiento indefinido", type: "switch" },
              {
                name: "estado", label: "Estado", type: "select", required: true,
                options: [
                  { label: "Activa", value: "activa" },
                  { label: "Suspendida", value: "suspendida" },
                  { label: "Completada", value: "completada" },
                ],
              },
              { name: "motivo_suspension", label: "Motivo de suspension", type: "text", placeholder: "Solo si esta suspendida" },
              { name: "fecha_suspension", label: "Fecha de suspension", type: "date" },
            ]}
          />
        )}

        {activeTab === "vigilancia" && (
          <SimpleCrudTab
            idPaciente={idPaciente}
            icon={<i className="fa-solid fa-eye" />}
            title="Planes de vigilancia"
            addLabel="Agregar plan"
            emptyText="Este paciente todavia no tiene planes de vigilancia registrados"
            fetchList={getPacientePlanesVigilancia}
            createItem={crearPacientePlanVigilancia}
            updateItem={editarPacientePlanVigilancia}
            deleteItem={eliminarPacientePlanVigilancia}
            idField="id_plan"
            columns={[
              { label: "Condicion", render: (r) => r.condicion },
              { label: "Periodicidad", render: (r) => `Cada ${r.periodicidad_meses} meses` },
              { label: "Ultima revision", render: (r) => fmtFecha(r.fecha_ultima_revision) },
              { label: "Proxima revision", render: (r) => fmtFecha(r.fecha_proxima_revision) },
            ]}
            fields={[
              { name: "condicion", label: "Condicion a vigilar", type: "text", required: true, placeholder: "Ej. Glaucoma, retinopatia diabetica..." },
              { name: "periodicidad_meses", label: "Periodicidad (meses)", type: "number", required: true, min: 1 },
              { name: "fecha_ultima_revision", label: "Fecha de ultima revision", type: "date", required: true },
              { name: "fecha_proxima_revision", label: "Fecha de proxima revision", type: "date", required: true },
            ]}
          />
        )}

        {activeTab === "referencias" && (
          <SimpleCrudTab
            idPaciente={idPaciente}
            icon={<i className="fa-solid fa-share-nodes" />}
            title="Referencias"
            addLabel="Agregar referencia"
            emptyText="Este paciente todavia no tiene referencias registradas"
            fetchList={getPacienteReferencias}
            createItem={crearPacienteReferencia}
            updateItem={editarPacienteReferencia}
            deleteItem={eliminarPacienteReferencia}
            idField="id_referencia"
            columns={[
              { label: "Colega", render: (r) => r.colega_nombre || r.nombre_colega_externo || "—" },
              { label: "Direccion", render: (r) => (r.direccion === "enviada" ? "Enviada" : "Recibida") },
              { label: "Motivo", render: (r) => r.motivo },
              { label: "Fecha", render: (r) => fmtFecha(r.fecha) },
            ]}
            fields={[
              { name: "nombre_colega_externo", label: "Colega (nombre)", type: "text", placeholder: "Ej. Dr. Juan Perez" },
              {
                name: "direccion", label: "Direccion de la referencia", type: "select", required: true,
                options: [{ label: "Enviada", value: "enviada" }, { label: "Recibida", value: "recibida" }],
              },
              { name: "motivo", label: "Motivo", type: "text", required: true },
            ]}
          />
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock" /></div>
              <h2 className="cd-card-title">
                Actividad del paciente
                {actividad.length > 0 && <span className="cd-actividad-count">{actividad.length}</span>}
              </h2>
            </div>
            {loadingActividad ? (
              <div className="cd-loading-wrap"><Spin size="large" /></div>
            ) : actividad.length === 0 ? (
              <Empty description="Sin actividad registrada aún" style={{ margin: "40px 0" }} />
            ) : (
              <div className="cd-actividad-list">
                {actividad.map((it, idx) => {
                  const cfg = getActionConfig(it.action);
                  return (
                    <div key={it.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{it.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtFechaHora(it.datetime)}</span>
                          {it.user_name && (
                            <div className="cd-actividad-user">
                              <span>{capitalize(it.user_name)}</span>
                              {it.user_email && <span className="cd-actividad-email">{it.user_email}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
